import type { DefaultLabelFormatterCallbackParams } from 'echarts';

import type { TAxisTooltipFormatterOptions } from '../types';
import { normalizeEChartsLabel } from './normalizeEChartsLabel';

type TAxisTooltipFormatterParams =
  | DefaultLabelFormatterCallbackParams
  | DefaultLabelFormatterCallbackParams[]
  | undefined
  | null;

type TTooltipPositionSize = {
  contentSize: [number, number];
  viewSize: [number, number];
};

type TBox = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

const TOOLTIP_EDGE_GAP = 8;

const getOverflowScrollParent = (element: HTMLElement): HTMLElement | null => {
  let parent = element.parentElement;

  while (parent && parent !== document.body) {
    const { overflowX, overflowY } = getComputedStyle(parent);
    if (overflowX === 'auto' || overflowX === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
      return parent;
    }
    parent = parent.parentElement;
  }

  return null;
};

const clampTooltipInBox = (point: number[], tooltipSize: [number, number], box: TBox, gap: number): number[] => {
  const [tooltipWidth, tooltipHeight] = tooltipSize;
  const [pointerLeft, pointerTop] = point;

  let tooltipLeft = pointerLeft + gap;
  let tooltipTop = pointerTop + gap;

  if (tooltipLeft + tooltipWidth > box.right - gap) {
    tooltipLeft = pointerLeft - tooltipWidth - gap;
  }
  if (tooltipTop + tooltipHeight > box.bottom - gap) {
    tooltipTop = pointerTop - tooltipHeight - gap;
  }

  tooltipLeft = Math.min(Math.max(tooltipLeft, box.left + gap), box.right - gap - tooltipWidth);
  tooltipTop = Math.min(Math.max(tooltipTop, box.top + gap), box.bottom - gap - tooltipHeight);

  return [tooltipLeft, tooltipTop];
};

const getVisibleChartBox = (chartDom: HTMLElement): TBox => {
  const chartRect = chartDom.getBoundingClientRect();
  const clipRect = getOverflowScrollParent(chartDom)?.getBoundingClientRect() ?? chartRect;

  return {
    left: Math.max(clipRect.left, 0) - chartRect.left,
    top: Math.max(clipRect.top, 0) - chartRect.top,
    right: Math.min(clipRect.right, window.innerWidth) - chartRect.left,
    bottom: Math.min(clipRect.bottom, window.innerHeight) - chartRect.top,
  };
};

export const createTooltipPositioning = () => {
  let chartDom: HTMLElement | null = null;

  const appendTo = (chartContainer: HTMLElement): HTMLElement => {
    chartDom = chartContainer;
    return document.body;
  };

  const position = (
    point: number[],
    _params: unknown,
    _dom: unknown,
    _rect: unknown,
    size: TTooltipPositionSize,
  ): number[] => {
    const box = chartDom
      ? getVisibleChartBox(chartDom)
      : { left: 0, top: 0, right: size.viewSize[0], bottom: size.viewSize[1] };

    return clampTooltipInBox(point, size.contentSize, box, TOOLTIP_EDGE_GAP);
  };

  return { appendTo, position };
};

export const normalizeAxisTooltipParams = (
  params: TAxisTooltipFormatterParams,
): DefaultLabelFormatterCallbackParams[] => {
  if (!params) return [];
  return Array.isArray(params) ? params : [params];
};

const getAxisTooltipParts = (
  params: TAxisTooltipFormatterParams,
  options: TAxisTooltipFormatterOptions,
): { header: string; lines: string[] } => {
  const listParams = normalizeAxisTooltipParams(params);
  if (!listParams.length) return { header: '', lines: [] };

  const header = options.getHeader(listParams);
  const lines = listParams.flatMap((param, index) => {
    const valueStr = options.formatValue(param, index);
    if (!valueStr) return [];

    const label = normalizeEChartsLabel(options.getSeriesLabel(param, index));
    return [label ? `${param.marker} ${label}: ${valueStr}` : `${param.marker} ${valueStr}`];
  });

  return { header, lines };
};

export const buildCardAxisTooltipHtml = (
  params: TAxisTooltipFormatterParams,
  options: TAxisTooltipFormatterOptions,
): string => {
  const { header, lines } = getAxisTooltipParts(params, options);
  if (!header && !lines.length) return '';

  const headerStyles = `font-size:12px;font-weight:500;color:#555;border-radius:4px;background:#f3f4f6;padding:4px 8px;${
    lines.length ? 'margin-bottom:4px;' : ''
  }`;
  const headerHtml = `<div style="${headerStyles}">${header}</div>`;
  if (!lines.length) return headerHtml;

  return `${headerHtml}<div style="padding:4px 8px 8px;">${lines.join('<br/>')}</div>`;
};
